<template>
  <a-select
    v-model:value="innerValue"
    :options="normalizedOptions.length ? normalizedOptions : undefined"
    :placeholder="placeholder"
    :disabled="disabled"
    :size="size"
    :allow-clear="allowClear"
    :show-search="showSearch"
    :filter-option="filterOption"
    :status="status"
    :loading="loading"
    :not-found-content="notFoundContent"
    class="ant-select-default"
    @change="onChange"
    @search="onSearch"
    @blur="onBlur"
  >
    <slot />
  </a-select>
</template>

<script lang="ts" setup>
import { computed } from "vue";
import type { SizeType } from "ant-design-vue/es/config-provider";
import type { SelectOption } from "./types";

interface Props {
  modelValue?: string | number | null;
  options?: SelectOption[];
  placeholder?: string;
  disabled?: boolean;
  size?: SizeType;
  allowClear?: boolean;
  showSearch?: boolean;
  loading?: boolean;
  status?: "" | "error" | "warning";
  notFoundContent?: string;
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: null,
  options: () => [],
  placeholder: "Chọn...",
  disabled: false,
  size: "middle",
  allowClear: true,
  showSearch: true,
  loading: false,
  status: "",
  notFoundContent: "Không có dữ liệu",
});

const emit = defineEmits<{
  (e: "update:modelValue", value: string | number | null): void;
  (e: "change", value: string | number | null, option?: SelectOption): void;
  (e: "search", value: string): void;
  (e: "blur", event: FocusEvent): void;
}>();

const normalizedOptions = computed(() =>
  (props.options || []).map((opt) => ({
    label: opt.label,
    value: opt.value,
    disabled: opt.disabled,
  }))
);

const removeAccent = (str: string) =>
  str
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");

const filterOption = (input: string, option: any) => {
  const label = String(option?.label ?? "");
  return removeAccent(label).includes(removeAccent(input));
};

const innerValue = computed({
  get: () => props.modelValue,
  set: (val: string | number | null) => {
    emit("update:modelValue", val);
  },
});

const onChange = (val: string | number | null, option: any) => {
  emit("update:modelValue", val);
  emit("change", val, option);
};

const onSearch = (val: string) => emit("search", val);
const onBlur = (e: FocusEvent) => emit("blur", e);
</script>

<style scoped>
.ant-select-default {
  width: 100%;
}
</style>

<!--
  Hướng dẫn sử dụng — Select / Dropdown
  Global: <h-select v-model="city" :options="[{ label: 'HN', value: 'hn' }]" />
  Import: import { SelectDefault } from '@/components/ant/select'

  Props: modelValue, options, placeholder, disabled, size, allowClear, showSearch, loading, status
  Events: update:modelValue, change, search, blur
  Docs: src/components/ant/select/README.md
-->
